import { useCallback, useEffect, useMemo, useRef } from "react";
import { EVENTS, STATUS, useJoyride } from "react-joyride";
import { TOUR_IDS } from "@demandproai/shared";
import { useTourContext } from "../contexts/TourContext";
import { TOUR_OPTIONS } from "../constants/tour";
import { buildCaseListTourSteps } from "../tours/caseListTourSteps";
import TourTooltip from "../components/tour/TourTooltip";
import useCertifiedLifecareCompany from "./useCertifiedLifecareCompany";
import useIsModalOpen from "./useIsModalOpen";
import useTourSpotlightSync from "./useTourSpotlightSync";
import useTourAnchorRecovery from "./useTourAnchorRecovery";

// Every stop here is read only, so unlike the edit case tour there is nothing to
// gate Next on and no wizard to drive. Anchors are either page furniture or the
// sample row, so Joyride's own target polling is the only wait needed.
const useCaseListTour = ({ isReady }) => {
   const {
      registerTour,
      requestedTourId,
      clearTourRequest,
      isTourPending,
      completeTour,
      dismissTour,
   } = useTourContext();
   const isCertifiedLifecareCompany = useCertifiedLifecareCompany();
   const isModalOpen = useIsModalOpen();

   const steps = useMemo(
      () => buildCaseListTourSteps({ hasAddCase: !isCertifiedLifecareCompany }),
      [isCertifiedLifecareCompany]
   );

   const isReplayRequested = requestedTourId === TOUR_IDS.CASE_LIST;

   // The table is display:none while the list loads, so its anchors measure as
   // nothing until the fetch settles
   const shouldAutoRun = isReady && isTourPending(TOUR_IDS.CASE_LIST);

   const handleTourEvent = useCallback(
      (data) => {
         if (data.type !== EVENTS.TOUR_END) {
            return;
         }

         if (data.status === STATUS.SKIPPED) {
            dismissTour(TOUR_IDS.CASE_LIST);
         } else {
            completeTour(TOUR_IDS.CASE_LIST);
         }

         clearTourRequest();
      },
      [completeTour, dismissTour, clearTourRequest]
   );

   const { Tour, controls, state, step } = useJoyride({
      steps,
      run: shouldAutoRun,
      continuous: true,
      tooltipComponent: TourTooltip,
      options: TOUR_OPTIONS,
      onEvent: handleTourEvent,
   });

   useTourSpotlightSync({ step, state });
   useTourAnchorRecovery({ step, state, controls });

   // Resuming is only correct for a tour that is mid run, so this is read rather
   // than depended on: a status change must not itself trigger a pause
   const isMidRunRef = useRef(false);
   isMidRunRef.current = [STATUS.RUNNING, STATUS.PAUSED].includes(state.status);

   // Download and version history dialogs render below the tour overlay, so a
   // tour left running would cover one the user opened. Pausing holds the index
   useEffect(() => {
      if (!isMidRunRef.current) {
         return;
      }

      if (isModalOpen) {
         controls.stop();
      } else {
         controls.start();
      }
   }, [isModalOpen, controls]);

   // A replay rewinds to the first stop rather than resuming where it stopped
   useEffect(() => {
      if (isReplayRequested) {
         controls.reset(true);
      }
   }, [isReplayRequested, controls]);

   useEffect(() => registerTour(TOUR_IDS.CASE_LIST), [registerTour]);

   return { Tour };
};

export default useCaseListTour;
